import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { FormGroup, FormControl, Validators, FormArray } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import {
  ICronograma,
  ICreateCronograma,
  IUpdateCronograma,
  TipoDeCronograma,
  IQueryParam,
  IUsuario,
  IFilter,
  IRecorrido,
  IUbicacion,
  IActivo,
  Dia,
} from 'modelos/src';
import { Subscription, firstValueFrom } from 'rxjs';
import { CronogramasService } from '../../cronogramas/cronogramas.service';
import { HelperService } from '../../../../../auxiliares/servicios/helper.service';
import { DialogService } from '../../../../../auxiliares/dialog/dialog.service';
import { MatDialogRef } from '@angular/material/dialog';
import { ParamsService } from '../../../../../auxiliares/servicios/params.service';
import { CrearEditarUbicacionComponent } from '../../../../standalone/ubicaciones/crear-editar-ubicacion/crear-editar-ubicacion.component';
import { Lista2Service } from '../../../../../auxiliares/servicios/lista2.service';
import { FormularioDialogService } from '../../../../../auxiliares/servicios/formulario-dialog.service';

@Component({
  selector: 'app-crear-editar-cronograma',
  templateUrl: './crear-editar-cronograma.component.html',
  styleUrl: './crear-editar-cronograma.component.scss',
  standalone: false,
})
export class CrearEditarCronogramaComponent implements OnInit, OnDestroy {
  dialogRef = inject<MatDialogRef<CrearEditarCronogramaComponent>>(
    MatDialogRef,
    { optional: true },
  );
  private service = inject(CronogramasService);
  private route = inject(ActivatedRoute);
  private lista2 = inject(Lista2Service);
  helper = inject(HelperService);
  private dialogService = inject(DialogService);
  private paramsService = inject(ParamsService);
  private formularios = inject(FormularioDialogService);

  public form?: FormGroup;
  public title?: string;
  public subtitle?: string;

  public id?: string | null;
  public cronograma?: ICronograma;
  // Selects
  public tipos: TipoDeCronograma[] = ['despacho', 'turnos'];
  public dias: Dia[] = [
    'Domingo',
    'Lunes',
    'Martes',
    'Miercoles',
    'Jueves',
    'Viernes',
    'Sabado',
  ];
  public terminales: IUbicacion[] = [];
  private terminales$?: Subscription;
  public vehiculos: IActivo[] = [];
  private vehiculos$?: Subscription;
  public recorridos: IRecorrido[] = [];
  private recorridos$?: Subscription;
  //
  public hoy = new Date();
  public hoyALas0 = new Date(
    this.hoy.getFullYear(),
    this.hoy.getMonth(),
    this.hoy.getDate(),
    0,
    0,
    0,
  );

  get configuracion() {
    return this.form?.get('configuracion') as FormGroup;
  }

  get periodos() {
    return this.form?.get('periodos') as FormArray;
  }

  public loading = true;

  private createForm() {
    this.form = new FormGroup({
      nombre: new FormControl(this.cronograma?.nombre, [Validators.required]),
      descripcion: new FormControl(this.cronograma?.descripcion),
      tipo: new FormControl(this.cronograma?.tipo || 'despacho', [
        Validators.required,
      ]),
      idUbicacion: new FormControl(this.cronograma?.idUbicacion, [
        Validators.required,
      ]),
      automatico: new FormControl(this.cronograma?.automatico),
      dias: new FormControl(this.cronograma?.dias),
      configuracion: new FormGroup({
        color: new FormControl(this.cronograma?.configuracion?.color),
        nombreParaMostrar: new FormControl(
          this.cronograma?.configuracion?.nombreParaMostrar,
        ),
      }),
      periodos: new FormArray(this.initPeriodos()),
    });
  }

  private initPeriodos() {
    const array: FormGroup[] = [];
    if (this.cronograma?.periodos) {
      for (const p of this.cronograma.periodos) {
        // Agrego los datos fijos de cada periodo
        const periodo = new FormGroup({});
        periodo.setControl('desde', new FormControl(p.desde));
        // Agrego los datos variables dentro de datos
        const datos = new FormGroup({});
        for (const key in p.datos) {
          if (Object.prototype.hasOwnProperty.call(p.datos, key)) {
            datos.setControl(key, new FormControl(p.datos[key]));
          }
        }
        // Agrego datos al periodo
        periodo.setControl('datos', datos);
        // Agrego el perido al grupo
        array.push(periodo);
      }
      // Devuelvo el array
      return array;
    } else {
      array.push(this.agregarPeriodoFormGroup());
      return array;
    }
  }

  public agregarPeriodoFormGroup() {
    return new FormGroup({
      desde: new FormControl(),
      datos: new FormGroup({
        idVehiculo: new FormControl([Validators.required]),
        idChofer: new FormControl(),
        idsRecorridos: new FormControl(null, [Validators.required]),
        idRecorridoActual: new FormControl(),
      }),
    });
  }

  public agregarPeriodo() {
    this.periodos.push(this.agregarPeriodoFormGroup());
  }

  public borrarPeriodo(i: number) {
    this.periodos.removeAt(i);
  }

  private async listar() {
    this.cronograma = await firstValueFrom(this.lista2.cronograma(this.id!));
  }

  private async listarTerminales() {
    const filtro: IFilter<IUbicacion> = {
      categoria: 'Terminal',
    };
    const query: IQueryParam = {
      filter: JSON.stringify(filtro),
      sort: 'nombre',
      limit: 0,
      select: 'nombre identificacion',
      includeChildren: true,
    };
    this.terminales$?.unsubscribe();
    this.terminales$ = this.lista2.terminals(query, 30).subscribe((data) => {
      this.terminales = data.datos;
    });
  }

  // Listados para los períodos
  // Vehículos
  private async listarColectivos() {
    const query: IQueryParam = {
      sort: 'identificacion',
      limit: 0,
      select: 'identificacion patente',
      includeChildren: true,
    };
    this.vehiculos$?.unsubscribe();
    this.vehiculos$ = this.lista2.vehiculos(query, 30).subscribe((data) => {
      this.vehiculos = data.datos;
    });
  }
  // Recorridos
  private async listarRecorridos() {
    const query: IQueryParam = {
      sort: 'nombre',
      limit: 0,
      select: 'nombre nombreFlota destino por',
      includeChildren: true,
    };
    this.recorridos$?.unsubscribe();
    this.recorridos$ = this.lista2.recorridos(query, 30).subscribe((data) => {
      this.recorridos = data.datos;
    });
  }

  public crearTerminal() {
    this.paramsService.setParams({ categoria: 'Terminal' });
    const dialog = this.formularios.abrir(CrearEditarUbicacionComponent, {
      tamano: 'mediano',
    });
    dialog.afterClosed().subscribe((res) => {
      if (res) {
        this.form.patchValue({ idUbicacion: res });
      }
    });
  }

  ///
  public async onSubmit() {
    this.loading = true;
    try {
      if (this.id) {
        const res = await this.dialogService.confirm(
          'Actualización',
          `¿Confirmás que querés actualizar el cronograma ${this.cronograma.nombre}?`,
        );
        if (!res) {
          this.loading = false;
          return;
        }
        // Update
        const data = this.getData();
        if (!data) {
          this.loading = false;
          return;
        }
        await this.service.update(this.id, data);
        this.helper.notifSuccess('Cronograma actualizado');
        HelperService.volver();
      } else {
        const res = await this.dialogService.confirm(
          'Creación',
          `¿Confirmás que querés crear un nuevo cronograma?`,
        );
        if (!res) {
          this.loading = false;
          return;
        }
        // Create
        const data = this.getData();
        if (!data) {
          this.loading = false;
          return;
        }
        await this.service.create(data);
        this.helper.notifSuccess('Cronograma creado');
        HelperService.volver();
      }
    } catch (error) {
      console.error(error);
      this.helper.notifError(error);
    }
    this.loading = false;
  }

  public getData(): ICreateCronograma | IUpdateCronograma {
    const data: ICreateCronograma | IUpdateCronograma = this.form?.value;
    return data;
  }

  private setTitles() {
    if (this.cronograma) {
      this.title = 'Editar Cronograma';
      this.subtitle = this.cronograma?.nombre;
    } else {
      this.title = 'Crear Cronograma';
    }
  }

  async ngOnInit() {
    const params = await firstValueFrom(this.route.paramMap);
    this.id = params.get('id');

    await Promise.all([
      this.listarTerminales(),
      this.listarColectivos(),
      this.listarRecorridos(),
    ]);

    this.setTitles();
    this.createForm();
    this.loading = false;
  }

  ngOnDestroy(): void {
    this.terminales$?.unsubscribe();
    this.vehiculos$?.unsubscribe();
    this.recorridos$?.unsubscribe();
  }
}
